import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, List, Typography, Space, Select, DatePicker, message, Grid } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, ShoppingCartOutlined, ShopOutlined, ExperimentOutlined, CarOutlined } from '@ant-design/icons';
import client from '../api/client';
import dayjs from 'dayjs';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    PieChart,
    Pie,
    Cell
} from 'recharts';

const { Title } = Typography;
const { Option } = Select;

const DashboardPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    // Chart State
    const [chartData, setChartData] = useState({ trend: [], distribution: [] });
    const [year, setYear] = useState(dayjs().year());
    const [month, setMonth] = useState(dayjs().month() + 1);
    const [productId, setProductId] = useState(null); // null = All Products
    const [products, setProducts] = useState([]);

    useEffect(() => {
        fetchStats();
        fetchProducts();
    }, []);

    useEffect(() => {
        fetchChartData();
    }, [year, month, productId]);

    const fetchStats = async () => {
        try {
            const { data } = await client.get('/dashboard/stats');
            setStats(data);
        } catch (error) {
            console.error('Dashboard error:', error);
            message.error(error.response?.data?.message || error.message || 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        try {
            const { data } = await client.get('/master/items');
            setProducts(data);
        } catch (error) { }
    };

    const fetchChartData = async () => {
        try {
            const params = { year };
            if (month && month !== 'all') params.month = month;
            if (productId) params.product_id = productId;

            const { data } = await client.get('/dashboard/charts', { params });
            setChartData(data);
        } catch (error) {
            console.error(error);
        }
    };

    const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
            <div style={{ textAlign: 'center' }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: 40, color: '#3b82f6', marginBottom: 16 }}></i>
                <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-secondary)' }}>Loading Insights...</div>
            </div>
        </div>
    );

    if (!stats) return <div style={{ padding: 40, textAlign: 'center' }}>Failed to load data.</div>;

    const { sales, purchases, operations, recent_sales, inventory, receivables, payables, low_stock } = stats;

    // Frontend safety filter — exclude livestock items by category or by known livestock names
    const LIVESTOCK_NAME_KEYWORDS = ['takal', 'dirasa', 'weaner', 'duck', 'boar', 'sow', 'piglet', 'pig', 'chicken', 'goat', 'cow', 'cattle', 'carabao', 'broiler', 'layer', 'fattener', 'gilts'];
    const low_stock_filtered = (low_stock || []).filter((item) => {
        const categoryName = (item.category_name || item.category?.name || '').toLowerCase();
        const categorySlug = (item.category_slug || item.category?.slug || '').toLowerCase();
        if (categoryName.includes('livestock') || categorySlug.includes('livestock')) {
            return false;
        }
        const itemName = (item.name || '').toLowerCase();
        return !LIVESTOCK_NAME_KEYWORDS.some((keyword) => itemName.includes(keyword));
    });

    const StatCard = ({ title, value, prefix, suffix, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} className="stat-value" style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {prefix}{parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{
                    backgroundColor: `${color}15`,
                    color: color,
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20
                }}>
                    {icon}
                </div>
            </div>
            {suffix && (
                <div style={{ marginTop: 16, display: 'flex', alignItems: 'center' }}>
                    <Tag color={suffix.type === 'up' ? 'success' : 'error'} bordered={false} style={{ borderRadius: 20, fontWeight: 600 }}>
                        {suffix.type === 'up' ? '+' : '-'}{suffix.value}%
                    </Tag>
                    <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>vs last month</Typography.Text>
                </div>
            )}
        </Card>
    );

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div style={{ maxWidth: isMobile ? '100%' : '60%' }}>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)', wordBreak: 'keep-all', whiteSpace: 'normal', lineHeight: 1.2 }}>
                        Analytics Dashboard
                    </Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 15 }}>
                        Overview of your farm operations and sales performance.
                    </Typography.Text>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, width: isMobile ? '100%' : 'auto' }}>
                    <DatePicker.YearPicker
                        value={dayjs().year(year)}
                        onChange={(date) => setYear(date ? date.year() : dayjs().year())}
                        style={{ borderRadius: 8, width: isMobile ? '100%' : 160 }}
                    />
                    <Select
                        value={month}
                        onChange={(value) => setMonth(value)}
                        placeholder="Filter Month"
                        allowClear
                        style={{ width: isMobile ? '100%' : 160, height: 40 }}
                        dropdownStyle={{ borderRadius: 8 }}
                    >
                        <Option key="all" value="all">All Months</Option>
                        {Array.from({ length: 12 }, (_, i) => (
                            <Option key={i + 1} value={i + 1}>{dayjs().month(i).format('MMMM')}</Option>
                        ))}
                    </Select>
                </div>
            </div>

            <Row gutter={[24, 24]}>
                <Col xs={24} sm={12} lg={8}>
                    <StatCard
                        title="Daily Sales"
                        value={sales.daily}
                        prefix="₱"
                        icon={<i className="fa-solid fa-cash-register"></i>}
                        color="#10b981"
                        subText="Today's sales total"
                    />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <StatCard
                        title="Monthly Sales"
                        value={sales.monthly}
                        prefix="₱"
                        icon={<i className="fa-solid fa-chart-line"></i>}
                        color="#3b82f6"
                        subText="Month-to-date sales"
                    />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <StatCard
                        title="Monthly Purchases"
                        value={purchases.monthly}
                        prefix="₱"
                        icon={<i className="fa-solid fa-cart-shopping"></i>}
                        color="#f59e0b"
                        subText="Month-to-date purchases"
                    />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <StatCard
                        title="Inventory Value"
                        value={inventory.total_value}
                        prefix="₱"
                        icon={<i className="fa-solid fa-boxes-stacked"></i>}
                        color="#8b5cf6"
                        subText="Total stock valuation"
                    />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <StatCard
                        title="Receivables"
                        value={receivables || 0}
                        prefix="₱"
                        icon={<i className="fa-solid fa-hand-holding-dollar"></i>}
                        color="#14b8a6"
                        subText="Outstanding invoices"
                    />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <StatCard
                        title="Payables"
                        value={payables || 0}
                        prefix="₱"
                        icon={<i className="fa-solid fa-file-invoice-dollar"></i>}
                        color="#ef4444"
                        subText="Supplier balances"
                    />
                </Col>
            </Row>

            <div style={{ marginTop: 24 }}>
                <Card
                    className="premium-card"
                    title={
                        <Space>
                            <i className="fa-solid fa-chart-area" style={{ color: '#3b82f6' }}></i>
                            <span style={{ fontWeight: 700 }}>Sales Performance</span>
                        </Space>
                    }
                    extra={
                        <Select
                            value={productId}
                            onChange={setProductId}
                            style={{ width: isMobile ? '100%' : 220 }}
                            allowClear
                            placeholder="Filter by Product"
                            showSearch
                            optionFilterProp="children"
                            dropdownStyle={{ borderRadius: 8 }}
                            dropdownMatchSelectWidth={220}
                        >
                            {products.map(p => <Option key={p.id} value={p.id}>{p.name}</Option>)}
                        </Select>
                    }
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={16}>
                            <div style={{ marginBottom: 20 }}>
                                <Typography.Text strong style={{ fontSize: 16 }}>Sales Trend ({month && month !== 'all' ? 'Daily' : 'Monthly'})</Typography.Text>
                            </div>
                            <ResponsiveContainer width="100%" height={isMobile ? 260 : 320}>
                                <AreaChart data={chartData.trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} dy={10} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} />
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <Tooltip
                                        contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                        formatter={(value) => [`₱${value.toLocaleString()}`, 'Total Sales']}
                                    />
                                    <Area type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Col>
                        <Col xs={24} md={8}>
                            <div style={{ marginBottom: 20 }}>
                                <Typography.Text strong style={{ fontSize: 16 }}>Product Distribution</Typography.Text>
                            </div>
                            {chartData.distribution && chartData.distribution.length > 0 ? (
                                <div style={{ height: isMobile ? 320 : 320, display: 'flex', flexDirection: 'column' }}>
                                    <ResponsiveContainer width="100%" height={isMobile ? 170 : 200}>
                                        <PieChart>
                                            <Pie
                                                data={chartData.distribution}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={isMobile ? 48 : 55}
                                                outerRadius={isMobile ? 75 : 88}
                                                paddingAngle={3}
                                                dataKey="value"
                                                nameKey="name"
                                            >
                                                {chartData.distribution.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                                formatter={(value, name) => [`₱${Number(value).toLocaleString()}`, name]}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div
                                        className="pie-legend-scroll"
                                        style={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflowY: 'auto',
                                            marginTop: 12,
                                            paddingRight: 6,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 8
                                        }}
                                    >
                                        {(() => {
                                            const total = chartData.distribution.reduce((sum, d) => sum + Number(d.value || 0), 0) || 1;
                                            return chartData.distribution.map((entry, index) => (
                                                <div
                                                    key={`legend-${index}`}
                                                    style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, lineHeight: 1.3 }}
                                                    title={`${entry.name} — ₱${Number(entry.value).toLocaleString()}`}
                                                >
                                                    <span
                                                        style={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            background: COLORS[index % COLORS.length],
                                                            flexShrink: 0
                                                        }}
                                                    />
                                                    <span
                                                        style={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            color: 'var(--text-secondary)'
                                                        }}
                                                    >
                                                        {entry.name}
                                                    </span>
                                                    <span style={{ fontWeight: 600, flexShrink: 0 }}>
                                                        {((Number(entry.value) / total) * 100).toFixed(1)}%
                                                    </span>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: isMobile ? 260 : 320, color: 'var(--text-secondary)', flexDirection: 'column' }}>
                                    <i className="fa-solid fa-folder-open" style={{ fontSize: 32, marginBottom: 12, opacity: 0.5 }}></i>
                                    No sales data for this period
                                </div>
                            )}
                        </Col>
                    </Row>
                </Card>
            </div>

            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                <Col xs={24} md={12}>
                    <Card
                        className="premium-card"
                        title={
                            <Space>
                                <i className="fa-solid fa-list-check" style={{ color: '#8b5cf6' }}></i>
                                <span style={{ fontWeight: 700 }}>Operational Status</span>
                            </Space>
                        }
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={8}>
                                <div style={{ background: 'var(--card-bg)', padding: '16px 20px', borderRadius: 12, border: '1px solid rgba(148, 163, 184, 0.2)', display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <div style={{ width: 44, height: 44, background: '#f0fdf4', color: '#10b981', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                                        <i className="fa-solid fa-seedling"></i>
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Crops</div>
                                        <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1 }}>{operations.active_crop_cycles}</div>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <div style={{ background: 'var(--card-bg)', padding: '16px 20px', borderRadius: 12, border: '1px solid rgba(148, 163, 184, 0.2)', display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <div style={{ width: 44, height: 44, background: '#fffbeb', color: '#f59e0b', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                                        <i className="fa-solid fa-cow"></i>
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Livestock</div>
                                        <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1 }}>{operations.active_livestock_batches}</div>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <div style={{ background: 'var(--card-bg)', padding: '16px 20px', borderRadius: 12, border: '1px solid rgba(148, 163, 184, 0.2)', display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <div style={{ width: 44, height: 44, background: '#eff6ff', color: '#3b82f6', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                                        <i className="fa-solid fa-truck"></i>
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Hauling</div>
                                        <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1 }}>{operations.pending_hauling}</div>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <div style={{ background: 'var(--card-bg)', padding: '16px 20px', borderRadius: 12, border: '1px solid rgba(148, 163, 184, 0.2)', display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <div style={{ width: 44, height: 44, background: '#fff7ed', color: '#ea580c', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                                        <i className="fa-solid fa-receipt"></i>
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Purchases</div>
                                        <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1 }}>{operations.pending_purchases || 0}</div>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <div style={{ background: 'var(--card-bg)', padding: '16px 20px', borderRadius: 12, border: '1px solid rgba(148, 163, 184, 0.2)', display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <div style={{ width: 44, height: 44, background: '#ecfeff', color: '#0e7490', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                                        <i className="fa-solid fa-file-invoice-dollar"></i>
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Sales</div>
                                        <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1 }}>{operations.pending_sales || 0}</div>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <div style={{ background: 'var(--card-bg)', padding: '16px 20px', borderRadius: 12, border: '1px solid rgba(148, 163, 184, 0.2)', display: 'flex', alignItems: 'center', gap: 16 }}>
                                    <div style={{ width: 44, height: 44, background: '#f5f3ff', color: '#8b5cf6', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                                        <i className="fa-solid fa-gears"></i>
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>Milling</div>
                                        <div style={{ fontSize: 22, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1 }}>{operations.active_milling}</div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card
                        className="premium-card"
                        title={
                            <Space>
                                <i className="fa-solid fa-receipt" style={{ color: '#10b981' }}></i>
                                <span style={{ fontWeight: 700 }}>Recent Sales</span>
                            </Space>
                        }
                    >
                        <List
                            itemLayout="horizontal"
                            dataSource={recent_sales}
                            renderItem={(item) => (
                                <List.Item style={{ padding: '12px 0' }}>
                                    <List.Item.Meta
                                        avatar={
                                            <div style={{ width: 40, height: 40, background: '#f0fdf4', color: '#16a34a', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <i className="fa-solid fa-shopping-basket"></i>
                                            </div>
                                        }
                                        title={<Typography.Text strong style={{ fontSize: 14 }}>{item.order_number}</Typography.Text>}
                                        description={
                                            <Space split={<Typography.Text type="secondary" style={{ fontSize: 12 }}>•</Typography.Text>}>
                                                <Typography.Text style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{item.partner.name}</Typography.Text>
                                                <Typography.Text style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{dayjs(item.order_date).format('MMM D')}</Typography.Text>
                                                <Typography.Text style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                                                    {item.items_summary || 'Items not specified'}
                                                    {item.items_count > 3 ? ` +${item.items_count - 3} more` : ''}
                                                </Typography.Text>
                                            </Space>
                                        }
                                    />
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)' }}>₱{parseFloat(item.total_amount).toLocaleString()}</div>
                                        <Tag
                                            color={item.status === 'CONFIRMED' || item.status === 'COMPLETED' ? 'success' : 'warning'}
                                            bordered={false}
                                            style={{ margin: 0, fontSize: 11, fontWeight: 600 }}
                                        >
                                            {item.status === 'CONFIRMED' || item.status === 'COMPLETED' ? 'Fulfilled' : item.status}
                                        </Tag>
                                    </div>
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>
            </Row>

            <div style={{ marginTop: 24 }}>
                <Card
                    className="premium-card"
                    title={
                        <Space>
                            <i className="fa-solid fa-triangle-exclamation" style={{ color: '#ef4444' }}></i>
                            <span style={{ fontWeight: 700 }}>Critical Stock Alerts</span>
                        </Space>
                    }
                >
                    <List
                        grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4, xl: 5 }}
                        dataSource={low_stock_filtered}
                        locale={{ emptyText: <div style={{ padding: 20 }}>No items currently below reorder levels. Good job!</div> }}
                        renderItem={(item) => (
                            <List.Item>
                                <div style={{ padding: '20px 16px', borderRadius: 12, border: '1px solid rgba(239, 68, 68, 0.3)', background: 'var(--card-bg)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden' }}>
                                    <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: '#ef4444' }}></div>
                                    <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 15, marginBottom: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(239, 68, 68, 0.12)', padding: '8px 12px', borderRadius: 8 }}>
                                            <Typography.Text style={{ fontSize: 11, color: '#991b1b', fontWeight: 600, textTransform: 'uppercase' }}>Available</Typography.Text>
                                            <Typography.Text style={{ fontSize: 18, fontWeight: 600, color: '#ef4444' }}>{parseFloat(item.current_stock).toFixed(1)}</Typography.Text>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px' }}>
                                            <Typography.Text style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>Threshold</Typography.Text>
                                            <Typography.Text style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>{parseFloat(item.reorder_level).toFixed(0)}</Typography.Text>
                                        </div>
                                    </div>
                                </div>
                            </List.Item>
                        )}
                    />
                </Card>
            </div>
        </div>
    );
};

export default DashboardPage;
